import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Avatar, 
  Button, 
  TextField, 
  Paper, 
  Box, 
  Typography, 
  Alert, 
  Snackbar,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Fade,
  Slide,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SecurityIcon from '@mui/icons-material/Security';
import FaceIcon from '@mui/icons-material/Face';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BugReportIcon from '@mui/icons-material/BugReport';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import TabView from './components/TabView/TabView';
import BiometricDebug from './components/BiometricDebug';
import Aurora from './components/AuroraBackground/aurora.jsx';
import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';
import { Capacitor } from '@capacitor/core';

// Configuration constants
const CONFIG = {
  BIOMETRIC: {
    REASON: '請使用指紋或面部識別登入 SuperCloud',
    TITLE: '生物識別登入',
    SUBTITLE: 'SuperCloud 3.1',
    DESCRIPTION: '使用您的生物識別資料快速登入'
  },
  CREDENTIALS: {
    USERNAME: 'argold',
    PASSCODE: '100511'
  },
  ALERT_DURATION: 6000
};

function SignIn() {
  const [username, setUsername] = useState('');
  const [passcode, setPasscode] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [isLoading, setIsLoading] = useState(false);
  const [isBiometricLoading, setIsBiometricLoading] = useState(false);
  
  // Biometric setup popup states
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [biometricSetupComplete, setBiometricSetupComplete] = useState(false);
  const [hasShownBiometricSetup, setHasShownBiometricSetup] = useState(false);
  

  
  // Debug mode state
  const [showDebugMode, setShowDebugMode] = useState(false);
  const [debugClickCount, setDebugClickCount] = useState(0);
  
  // Console debug logs state
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebugLogs, setShowDebugLogs] = useState(false);
  const debugLogsRef = useRef([]);
  const maxLogs = 50; // 最多保留50條日誌
  
  // Auto-login state for testing
  const [autoLoginEnabled, setAutoLoginEnabled] = useState(() => {
    return localStorage.getItem('auto_login_enabled') === 'true';
  });
  const [autoLoginInProgress, setAutoLoginInProgress] = useState(false);
  const autoLoginTimeoutRef = useRef(null);

  // 添加調試日誌函數 - 使用 useCallback 優化性能
  const addDebugLog = useCallback((level, message, data = null) => {
    const timestamp = new Date().toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      level,
      message,
      data: data ? JSON.stringify(data, null, 2) : null
    };
    
    // 更新日誌數組
    debugLogsRef.current = [logEntry, ...debugLogsRef.current.slice(0, maxLogs - 1)];
    setDebugLogs([...debugLogsRef.current]);
    
    // 同時輸出到瀏覽器控制台
    const consoleMessage = `[${timestamp}] ${message}`;
    switch (level) {
      case 'error':
        console.error(consoleMessage, data);
        break;
      case 'warn':
        console.warn(consoleMessage, data);
        break;
      case 'info':
        console.info(consoleMessage, data);
        break;
      default:
        console.log(consoleMessage, data);
    }
  }, [maxLogs]);

  // 複製日誌到剪貼板
  const copyLogsToClipboard = async () => {
    try {
      const logsText = debugLogs.map(log => {
        let text = `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.message}`;
        if (log.data) {
          text += `\n${log.data}`;
        }
        return text;
      }).join('\n\n');
      
      await navigator.clipboard.writeText(logsText);
      showAlertMessage('調試日誌已複製到剪貼板', 'success');
    } catch (error) {
      addDebugLog('error', '複製日誌失敗', { error: error.message });
      showAlertMessage('複製失敗，請手動選擇文字複製', 'error');
    }
  };

  // 清除調試日誌
  const clearDebugLogs = () => {
    debugLogsRef.current = [];
    setDebugLogs([]);
    addDebugLog('info', '調試日誌已清除');
  };

  // 檢查生物識別可用性
  useEffect(() => {
    addDebugLog('info', '開始初始化 SignIn 組件');
    
    // 只在原生平台上檢查生物識別
    if (Capacitor.isNativePlatform()) {
      addDebugLog('info', '檢測到原生平台，開始檢查生物識別可用性');
      checkBiometricAvailability();
    } else {
      addDebugLog('warn', '非原生平台，跳過生物識別檢查', {
        platform: Capacitor.getPlatform(),
        userAgent: navigator.userAgent
      });
      setBiometricAvailable(false);
    }
  }, []);

  // 記錄登入狀態變化
  useEffect(() => {
    if (isLoggedIn) {
      addDebugLog('info', '用戶已登入，渲染 TabView 組件');
    }
  }, [isLoggedIn]);

  // 記錄調試模式狀態變化
  useEffect(() => {
    if (showDebugMode) {
      addDebugLog('info', '顯示調試模式');
    }
  }, [showDebugMode]);

  // Auto-login functionality
  useEffect(() => {
    if (autoLoginEnabled && !isLoggedIn && !autoLoginInProgress) {
      addDebugLog('info', '自動登入已啟用，開始自動登入流程');
      setAutoLoginInProgress(true);
      
      // Auto-fill credentials
      setUsername(CONFIG.CREDENTIALS.USERNAME);
      setPasscode(CONFIG.CREDENTIALS.PASSCODE);
      addDebugLog('info', '已自動填入登入憑證');
      
      // Auto-submit after delay
      autoLoginTimeoutRef.current = setTimeout(() => {
        addDebugLog('info', '執行自動登入提交');
        handleLogin();
        setAutoLoginInProgress(false);
      }, 1500); // 1.5 second delay
    }
    
    return () => {
      if (autoLoginTimeoutRef.current) {
        clearTimeout(autoLoginTimeoutRef.current);
        autoLoginTimeoutRef.current = null;
      }
    };
  }, [autoLoginEnabled, isLoggedIn, autoLoginInProgress]);

  // Handle auto-login toggle
  const handleAutoLoginToggle = (event) => {
    const enabled = event.target.checked;
    setAutoLoginEnabled(enabled);
    localStorage.setItem('auto_login_enabled', enabled.toString());
    
    if (enabled) {
      addDebugLog('info', '自動登入已啟用');
      showAlertMessage('自動登入已啟用 (測試用)', 'info');
    } else {
      addDebugLog('info', '自動登入已停用');
      showAlertMessage('自動登入已停用', 'info');
      setAutoLoginInProgress(false);
      if (autoLoginTimeoutRef.current) {
        clearTimeout(autoLoginTimeoutRef.current);
        autoLoginTimeoutRef.current = null;
      }
    }
  };

  const checkBiometricAvailability = async () => {
    try {
      addDebugLog('info', '開始檢查生物識別可用性');
      
      const platformInfo = {
        platform: Capacitor.getPlatform(),
        isNative: Capacitor.isNativePlatform(),
        userAgent: navigator.userAgent
      };
      addDebugLog('info', '平台資訊', platformInfo);
      
      const result = await NativeBiometric.isAvailable();
      addDebugLog('info', '生物識別檢查原始結果', result);
      
      // 更寬鬆的可用性檢查
      const isAvailable = result && (
        result.isAvailable === true || 
        result.available === true ||
        (result.biometryType && result.biometryType !== 'none')
      );
      
      addDebugLog('info', '生物識別可用性判斷', {
        isAvailable,
        biometryType: result?.biometryType,
        rawResult: result
      });
      
      setBiometricAvailable(isAvailable);
      setBiometricType(result?.biometryType || null);
      
      if (isAvailable) {
        addDebugLog('info', `生物識別可用: ${result.biometryType}`);
        
        // 檢查設備是否已設置生物識別憑證
        try {
          const hasCredentials = await NativeBiometric.getCredentials({
            server: 'supercloud.com'
          }).catch(() => null);
          
          addDebugLog('info', '憑證檢查結果', {
            hasCredentials: !!hasCredentials,
            credentials: hasCredentials
          });
        } catch (credError) {
          addDebugLog('warn', '憑證檢查失敗', {
            error: credError.message || credError,
            stack: credError.stack
          });
        }
        
        // 檢查用戶是否已看過生物識別設置
        const hasSeenSetup = localStorage.getItem('biometric_setup_shown');
        addDebugLog('info', '生物識別設置狀態', {
          hasSeenSetup: !!hasSeenSetup,
          hasShownBiometricSetup
        });
        
        if (!hasSeenSetup && !hasShownBiometricSetup) {
          addDebugLog('info', '準備顯示生物識別設置對話框');
          setTimeout(() => {
            setShowBiometricSetup(true);
            setHasShownBiometricSetup(true);
            addDebugLog('info', '已顯示生物識別設置對話框');
          }, 1500);
        }
      } else {
        const reason = result?.reason || result?.error || '未知原因';
        addDebugLog('warn', '生物識別不可用', {
          reason,
          result
        });
        
        // 只在特定情況下顯示警告訊息
        if (typeof reason === 'string') {
          if (reason.includes('not enrolled') || reason.includes('no biometrics')) {
            addDebugLog('info', '提示: 請先在設備設置中註冊指紋或面部識別');
          } else if (reason.includes('permission')) {
            showAlertMessage('請允許應用程式使用生物識別功能', 'warning');
            addDebugLog('warn', '生物識別權限被拒絕');
          }
        }
      }
    } catch (error) {
      addDebugLog('error', '生物識別檢查失敗', {
        message: error.message || error,
        stack: error.stack,
        name: error.name,
        code: error.code
      });
      
      setBiometricAvailable(false);
      setBiometricType(null);
      
      // 更精確的錯誤處理
      const errorMessage = error.message || error.toString();
      
      if (errorMessage.includes('not implemented') || errorMessage.includes('not supported')) {
        addDebugLog('info', '此設備不支援生物識別功能');
      } else if (errorMessage.includes('permission') || errorMessage.includes('denied')) {
        showAlertMessage('請允許應用程式使用生物識別功能', 'warning');
        addDebugLog('warn', '生物識別權限問題');
      } else if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
        showAlertMessage('網路連線問題，請檢查網路設定', 'error');
        addDebugLog('error', '網路連線問題');
      } else {
        addDebugLog('info', '生物識別初始化失敗，將使用傳統登入方式');
      }
    }
  };

  const handleBiometricSetupAccept = async () => {
    try {
      addDebugLog('info', '開始生物識別設置驗證');
      
      const verifyParams = {
        reason: CONFIG.BIOMETRIC.REASON,
        title: '設置生物識別登入',
        subtitle: CONFIG.BIOMETRIC.SUBTITLE,
        description: '請驗證您的身份以啟用生物識別登入'
      };
      
      addDebugLog('info', '生物識別設置驗證參數', verifyParams);
      
      // 根據官方文檔，verifyIdentity 返回 Promise<void>
      // 成功時 Promise resolve（無返回值），失敗時 Promise reject
      try {
        await NativeBiometric.verifyIdentity(verifyParams);
        
        // 如果執行到這裡，表示驗證成功
        addDebugLog('info', '生物識別設置驗證成功，開始儲存憑證');
        
        // 儲存憑證以供後續使用
        try {
          const credentialsParams = {
            username: CONFIG.CREDENTIALS.USERNAME,
            password: CONFIG.CREDENTIALS.PASSCODE,
            server: 'supercloud.com'
          };
          
          addDebugLog('info', '準備儲存生物識別憑證', {
            server: credentialsParams.server,
            username: credentialsParams.username
          });
          
          await NativeBiometric.setCredentials(credentialsParams);
          addDebugLog('info', '生物識別憑證已成功儲存');
        } catch (credError) {
          addDebugLog('warn', '憑證儲存失敗', {
            error: credError.message || credError,
            stack: credError.stack
          });
          // 即使憑證儲存失敗，仍然可以繼續設置
        }
        
        setBiometricSetupComplete(true);
        localStorage.setItem('biometric_setup_shown', 'true');
        localStorage.setItem('biometric_enabled', 'true');
        
        addDebugLog('info', '生物識別設置完成，已更新本地儲存');
        showAlertMessage('生物識別登入已成功啟用！您現在可以使用生物識別快速登入', 'success');
        
        setTimeout(() => {
          setShowBiometricSetup(false);
          addDebugLog('info', '生物識別設置對話框已關閉');
        }, 2000);
      } catch (setupError) {
        addDebugLog('error', '生物識別設置驗證失敗', {
          error: setupError?.message || setupError,
          errorCode: setupError?.code
        });
        
        let errorMessage = '生物識別設置失敗，請重試';
        if (setupError?.code === 16) {
          errorMessage = '生物識別設置被取消';
        }
        
        showAlertMessage(errorMessage, 'error');
        return;
      }
    } catch (error) {
      addDebugLog('error', '生物識別設置過程中發生錯誤', {
        message: error.message || error,
        stack: error.stack,
        name: error.name,
        code: error.code
      });
      
      // 提供更詳細的設置錯誤訊息
      let errorMessage = '生物識別設置失敗';
      const errorStr = (error.message || error.toString()).toLowerCase();
      
      if (errorStr.includes('cancelled') || errorStr.includes('canceled') || errorStr.includes('user_cancel')) {
        errorMessage = '生物識別設置已取消，您可以稍後在設置中啟用';
        addDebugLog('info', '用戶取消了生物識別設置');
      } else if (errorStr.includes('not available') || errorStr.includes('unavailable') || errorStr.includes('not_available')) {
        errorMessage = '生物識別功能暫時不可用，請稍後重試';
        addDebugLog('warn', '生物識別功能不可用');
      } else if (errorStr.includes('not enrolled') || errorStr.includes('no biometrics') || errorStr.includes('not_enrolled')) {
        errorMessage = '請先在設備設置中註冊指紋或面部識別';
        addDebugLog('warn', '設備未註冊生物識別');
      } else if (errorStr.includes('permission') || errorStr.includes('denied') || errorStr.includes('permission_denied')) {
        errorMessage = '請允許應用程式使用生物識別功能';
        addDebugLog('warn', '生物識別權限被拒絕');
      } else if (errorStr.includes('not implemented') || errorStr.includes('not_implemented')) {
        errorMessage = '此設備不支援生物識別功能';
        addDebugLog('warn', '設備不支援生物識別');
      } else {
        errorMessage = '生物識別設置失敗，請稍後重試';
        addDebugLog('error', '未知的生物識別設置錯誤');
      }
      
      showAlertMessage(errorMessage, 'error');
    }
  };

  const handleBiometricSetupDecline = () => {
    addDebugLog('info', '用戶拒絕設置生物識別');
    localStorage.setItem('biometric_setup_shown', 'true');
    setShowBiometricSetup(false);
    showAlertMessage('您可以稍後在設置中啟用生物識別登入', 'info');
  };

  const getBiometricTypeDisplay = () => {
    switch (biometricType) {
      case BiometryType.FINGERPRINT:
        return { 
          icon: <FingerprintIcon sx={{ fontSize: 24 }} />, 
          text: '指紋登入',
          setupIcon: <FingerprintIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
          setupText: '指紋識別'
        };
      case BiometryType.FACE_ID:
        return { 
          icon: <FaceIcon sx={{ fontSize: 24 }} />, 
          text: '面部識別登入',
          setupIcon: <FaceIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
          setupText: 'Face ID'
        };
      case BiometryType.TOUCH_ID:
        return { 
          icon: <FingerprintIcon sx={{ fontSize: 24 }} />, 
          text: 'Touch ID 登入',
          setupIcon: <FingerprintIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
          setupText: 'Touch ID'
        };
      default:
        return { 
          icon: <SecurityIcon sx={{ fontSize: 24 }} />, 
          text: '生物識別登入',
          setupIcon: <SecurityIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
          setupText: '生物識別'
        };
    }
  };

  const handleBiometricLogin = async () => {
    addDebugLog('info', '開始生物識別登入流程');
    
    // 檢查平台和可用性
    if (!Capacitor.isNativePlatform()) {
      const message = '生物識別功能僅在行動裝置上可用';
      addDebugLog('warn', message, {
        platform: Capacitor.getPlatform(),
        isNative: Capacitor.isNativePlatform()
      });
      showAlertMessage(message, 'warning');
      return;
    }
    
    if (!biometricAvailable) {
      const message = '生物識別功能不可用，請使用密碼登入';
      addDebugLog('warn', message, {
        biometricAvailable,
        biometricType
      });
      showAlertMessage(message, 'warning');
      return;
    }

    setIsBiometricLoading(true);
    addDebugLog('info', '設置生物識別載入狀態為 true');
    
    try {
      const verifyParams = {
        reason: CONFIG.BIOMETRIC.REASON,
        title: CONFIG.BIOMETRIC.TITLE,
        subtitle: CONFIG.BIOMETRIC.SUBTITLE,
        description: CONFIG.BIOMETRIC.DESCRIPTION
      };
      
      addDebugLog('info', '生物識別驗證參數', verifyParams);
      
      // 根據官方文檔，verifyIdentity 返回 Promise<void>
      // 成功時 Promise resolve（無返回值），失敗時 Promise reject
      try {
        await NativeBiometric.verifyIdentity(verifyParams);
        
        // 如果執行到這裡，表示驗證成功
        addDebugLog('info', '生物識別驗證成功，準備登入');
        

        
        setIsLoggedIn(true);
        showAlertMessage('生物識別登入成功！', 'success');
        addDebugLog('info', '用戶已成功登入');
        

        
      } catch (verifyError) {
        // 驗證失敗或被取消
        addDebugLog('error', '生物識別驗證失敗', {
          error: verifyError?.message || verifyError,
          errorCode: verifyError?.code,
          errorType: typeof verifyError
        });

        // 根據錯誤代碼提供具體的錯誤訊息
        let errorMessage = '生物識別驗證失敗，請重試或使用密碼登入';
        
        if (verifyError?.code) {
          switch (verifyError.code) {
            case 16: // User Cancel
              errorMessage = '生物識別驗證已取消，請重試或使用密碼登入';
              break;
            case 15: // System Cancel
              errorMessage = '系統取消了生物識別驗證，請重試';
              break;
            case 10: // Authentication Failed
              errorMessage = '生物識別驗證失敗，請重試或使用密碼登入';
              break;
            case 1: // Biometrics Unavailable
              errorMessage = '生物識別功能暫時無法使用，請使用密碼登入';
              break;
            case 3: // Biometrics Not Enrolled
              errorMessage = '尚未設定生物識別，請先在系統設定中啟用';
              break;
            default:
              errorMessage = `生物識別驗證失敗 (錯誤代碼: ${verifyError.code})，請重試或使用密碼登入`;
          }
        }
        
        showAlertMessage(errorMessage, 'error');
      }
    } catch (error) {
      addDebugLog('error', '生物識別登入過程中發生錯誤', {
        message: error.message || error,
        stack: error.stack,
        name: error.name,
        code: error.code,
        timestamp: new Date().toISOString()
      });
      
      // 提供更詳細和用戶友好的錯誤訊息
      let errorMessage = '生物識別登入失敗';
      let severity = 'error';
      
      const errorStr = (error.message || error.toString()).toLowerCase();
      
      if (errorStr.includes('cancelled') || errorStr.includes('canceled') || errorStr.includes('user_cancel')) {
        errorMessage = '生物識別驗證已取消';
        severity = 'info';
        addDebugLog('info', '用戶取消了生物識別驗證');

      } else if (errorStr.includes('not available') || errorStr.includes('unavailable') || errorStr.includes('not_available')) {
        errorMessage = '生物識別功能暫時不可用，請使用密碼登入';
        severity = 'warning';
        addDebugLog('warn', '生物識別功能暫時不可用');
      } else if (errorStr.includes('failed') || errorStr.includes('authentication_failed') || errorStr.includes('auth_failed')) {
        errorMessage = '生物識別驗證失敗，請重試或使用密碼登入';
        severity = 'error';
        addDebugLog('error', '生物識別驗證失敗');
      } else if (errorStr.includes('too many attempts') || errorStr.includes('lockout') || errorStr.includes('locked_out')) {
        errorMessage = '嘗試次數過多，請稍後再試或使用密碼登入';
        severity = 'warning';
        addDebugLog('warn', '生物識別嘗試次數過多');
      } else if (errorStr.includes('not enrolled') || errorStr.includes('no biometrics') || errorStr.includes('not_enrolled')) {
        errorMessage = '請先在設備設置中註冊指紋或面部識別';
        severity = 'info';
        addDebugLog('info', '設備未註冊生物識別');
      } else if (errorStr.includes('permission') || errorStr.includes('denied') || errorStr.includes('permission_denied')) {
        errorMessage = '請允許應用程式使用生物識別功能';
        severity = 'warning';
        addDebugLog('warn', '生物識別權限被拒絕');
      } else if (errorStr.includes('hardware') || errorStr.includes('sensor') || errorStr.includes('hardware_unavailable')) {
        errorMessage = '生物識別硬體暫時無法使用，請使用密碼登入';
        severity = 'warning';
        addDebugLog('warn', '生物識別硬體問題');
      } else if (errorStr.includes('timeout') || errorStr.includes('user_fallback')) {
        errorMessage = '生物識別驗證超時，請重試';
        severity = 'warning';
        addDebugLog('warn', '生物識別驗證超時');
      } else if (errorStr.includes('not implemented') || errorStr.includes('not_implemented')) {
        errorMessage = '此設備不支援生物識別功能';
        severity = 'warning';
        addDebugLog('warn', '設備不支援生物識別');
      } else {
        errorMessage = `生物識別登入失敗: ${error.message || error}`;
        addDebugLog('error', '未知的生物識別錯誤類型', { errorStr });
      }
      
      showAlertMessage(errorMessage, severity);
      
    } finally {
      setIsBiometricLoading(false);
      addDebugLog('info', '生物識別載入狀態已重置');
      

    }
  };

  const showAlertMessage = (message, severity = 'info') => {
    addDebugLog('info', `顯示警告訊息: [${severity}] ${message}`);
    setAlertMessage(message);
    setAlertSeverity(severity);
    setShowAlert(true);
  };

  const handleLogin = async () => {
    addDebugLog('info', '開始傳統登入流程', {
      username: username,
      hasPasscode: !!passcode
    });
    
    if (!username.trim() || !passcode.trim()) {
      const message = '請輸入用戶名和密碼';
      addDebugLog('warn', message);
      showAlertMessage(message, 'warning');
      return;
    }

    setIsLoading(true);
    addDebugLog('info', '設置登入載入狀態為 true');
    
    try {
      // 模擬網路延遲
      addDebugLog('info', '模擬網路延遲 500ms');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const isValidCredentials = username === CONFIG.CREDENTIALS.USERNAME && passcode === CONFIG.CREDENTIALS.PASSCODE;
      
      addDebugLog('info', '驗證登入憑證', {
        inputUsername: username,
        expectedUsername: CONFIG.CREDENTIALS.USERNAME,
        isValidCredentials
      });
      
      if (isValidCredentials) {
        addDebugLog('info', '登入憑證驗證成功');
        setIsLoggedIn(true);
        showAlertMessage('登入成功！', 'success');
        addDebugLog('info', '用戶已成功登入');
      } else {
        addDebugLog('warn', '登入憑證驗證失敗');
        showAlertMessage('用戶名或密碼錯誤，請聯繫管理員', 'error');
      }
    } catch (error) {
      addDebugLog('error', '登入過程中發生錯誤', {
        message: error.message || error,
        stack: error.stack
      });
      
      // 提供更詳細的登入錯誤訊息
      let errorMessage = '登入失敗，請稍後重試';
      if (error && error.message) {
        if (error.message.includes('network') || error.message.includes('timeout')) {
          errorMessage = '網路連線問題，請檢查網路設定後重試';
          addDebugLog('error', '網路連線問題');
        } else if (error.message.includes('server')) {
          errorMessage = '伺服器暫時無法使用，請稍後重試';
          addDebugLog('error', '伺服器問題');
        } else {
          errorMessage = '登入過程中發生未知錯誤，請聯繫技術支援';
          addDebugLog('error', '未知登入錯誤');
        }
      }
      
      showAlertMessage(errorMessage, 'error');
      
    } finally {
      setIsLoading(false);
      addDebugLog('info', '登入載入狀態已重置');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    addDebugLog('info', '表單提交事件觸發');
    handleLogin();
  };
  
  const handleVersionClick = () => {
    const newCount = debugClickCount + 1;
    setDebugClickCount(newCount);
    addDebugLog('info', `版本號點擊次數: ${newCount}/7`);
    
    if (newCount >= 7) {
      setShowDebugMode(true);
      showAlertMessage('調試模式已啟用', 'info');
      setDebugClickCount(0);
      addDebugLog('info', '調試模式已啟用');
    }
  };

  if (isLoggedIn) {
    try {
      console.log('Rendering TabView component...');
      return (
        <Box sx={{ width: '100%', height: '100vh' }}>
          <TabView />
        </Box>
      );
    } catch (error) {
      console.error('Error rendering TabView:', error);
      return (
        <Box sx={{ 
          minHeight: '100vh', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="error" gutterBottom>
              應用程式載入錯誤
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {error.message || '未知錯誤'}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => {
                setIsLoggedIn(false);
                window.location.reload();
              }}
            >
              重新載入
            </Button>
          </Paper>
        </Box>
      );
    }
  }
  
  if (showDebugMode) {
    return (
      <Box sx={{ p: 2 }}>
        <Button 
          variant="outlined" 
          onClick={() => {
            setShowDebugMode(false);
            addDebugLog('info', '退出調試模式');
          }}
          sx={{ mb: 2 }}
        >
          返回登入頁面
        </Button>
        <BiometricDebug />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      backgroundColor: '#000000'
    }}>
      <Aurora 
        colorStops={["#2557ef", "#7cff67", "#5227ff"]} 
        blend={0.5} 
        amplitude={2.0} 
        speed={0.8}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 0
        }}
      />
      <Fade in={true} timeout={800}>
        <Paper 
          elevation={24} 
          sx={{ 
            p: 4, 
            maxWidth: 400, 
            width: '100%',
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            position: 'relative',
            zIndex: 1,
            mx: 'auto',
            my: 'auto'
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Welcome
            </Typography>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                mb: 0.5
              }}
            >
              Greens Management
            </Typography>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              System
            </Typography>
          </Box>

          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ 
              bgcolor: 'primary.main', 
              width: 56, 
              height: 56, 
              fontSize: 32,
              boxShadow: 3
            }}>
              🦸🏻
            </Avatar>
            <Avatar sx={{ 
              bgcolor: 'secondary.main', 
              width: 56, 
              height: 56, 
              fontSize: 32,
              boxShadow: 3
            }}>
              🐭
            </Avatar>
            <Avatar sx={{ 
              bgcolor: 'pink', 
              width: 56, 
              height: 56, 
              fontSize: 32,
              boxShadow: 3
            }}>
              🐷
            </Avatar>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="用戶名"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="passcode"
              label="密碼"
              type="password"
              id="passcode"
              autoComplete="current-password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
                  },
                  flex: 1,
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    登入中...
                  </Box>
                ) : (
                  '登入'
                )}
              </Button>

              {/* 生物識別登入小按鈕 */}
              {biometricAvailable && Capacitor.isNativePlatform() && (
                <Tooltip title={getBiometricTypeDisplay().text}>
                  <IconButton
                    disabled={isBiometricLoading}
                    onClick={handleBiometricLogin}
                    sx={{
                      height: '100%',
                      minHeight: 60, // 確保與登入按鈕高度一致
                      aspectRatio: '1 / 1', // 確保為正方形
                      border: '2px solid',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      borderRadius: 2, // 與登入按鈕一致的圓角
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'rgba(103, 126, 234, 0.1)',
                        color: 'primary.dark',
                      },
                      '&:disabled': {
                        borderColor: 'action.disabled',
                        color: 'action.disabled',
                      },
                    }}
                  >
                    {isBiometricLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <FingerprintIcon sx={{ fontSize: 28 }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Auto-login toggle for testing - Only show in development */}
          {import.meta.env.DEV && (
            <Box sx={{ mt: 2, width: '100%' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoLoginEnabled}
                    onChange={handleAutoLoginToggle}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      自動登入 (測試用)
                    </Typography>
                    {autoLoginEnabled && (
                      <Chip
                        label="啟用"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Box>
                }
                sx={{ 
                  width: '100%',
                  justifyContent: 'space-between',
                  ml: 0,
                  mr: 0,
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }
                }}
              />
            </Box>
          )}

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                fontSize: '0.9rem',
                lineHeight: 1.6
              }}
            >
              <span 
                onClick={handleVersionClick}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title={`點擊 ${7 - debugClickCount} 次啟用調試模式`}
              >
                Version 3.1
              </span>{'\n'}
              <Box component="span" display="block">
                (🐷😵 Special Edition)
              </Box>
              <Box component="span" display="block" sx={{ mt: 1 }}>
                ©2025 SuperCloud Team
              </Box>
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* 生物識別設置對話框 */}
      <Dialog 
        open={showBiometricSetup} 
        onClose={() => {}} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {getBiometricTypeDisplay().setupIcon}
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              啟用{getBiometricTypeDisplay().setupText}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
            您的設備支援{getBiometricTypeDisplay().setupText}功能
          </Typography>
          
          <Card sx={{ mb: 3, backgroundColor: 'rgba(103, 126, 234, 0.1)' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                {biometricSetupComplete ? (
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
                ) : (
                  getBiometricTypeDisplay().setupIcon
                )}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {biometricSetupComplete ? '設置完成' : '快速登入'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {biometricSetupComplete 
                  ? '您現在可以使用生物識別快速登入' 
                  : '使用您的生物識別資料快速安全地登入應用程式'
                }
              </Typography>
            </CardContent>
          </Card>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            啟用後，您可以使用{getBiometricTypeDisplay().setupText}代替密碼登入
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button 
            onClick={handleBiometricSetupDecline}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            稍後設置
          </Button>
          <Button 
            onClick={handleBiometricSetupAccept}
            variant="contained"
            sx={{ 
              minWidth: 100,
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
              }
            }}
          >
            立即啟用
          </Button>
        </DialogActions>
      </Dialog>

      {/* 警告訊息 Snackbar */}
      <Snackbar 
        open={showAlert} 
        autoHideDuration={CONFIG.ALERT_DURATION} 
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowAlert(false)} 
          severity={alertSeverity} 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SignIn;